import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_STOCK_TRANSACTIONS } from '../graphql/queries/stocks';
import { SET_STOCK_TRANSACTION_HIDDEN } from '../graphql/mutations/stockTransactions';
import TransactionRow, { TransactionMobileCard } from './TransactionRow';
import StockTransactionModal from './StockTransactionModal';
import StockDeleteTransactionDialog from './StockDeleteTransactionDialog';
import LoadingSpinner from './LoadingSpinner';
import useStockLocations from '../hooks/useStockLocations';
import ErrorAlert from './ErrorAlert';
import StockAccountingWarning from './StockAccountingWarning';

const StockTransactions = ({
  stock,
  portfolioId,
  currentPrice,
  portfolioCurrentPrice,
  onImported,
  onChanged,
}) => {
  const [stockId, setStockId] = useState(stock.stockId || stock.id);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedTransactionId, setExpandedTransactionId] = useState(null);
  const [setTransactionHidden] = useMutation(SET_STOCK_TRANSACTION_HIDDEN);
  const { locations } = useStockLocations(portfolioId);

  useEffect(() => {
    setStockId(stock.stockId || stock.id);
  }, [stock.id, stock.stockId]);

  const { data, loading, error, refetch } = useQuery(GET_STOCK_TRANSACTIONS, {
    variables: { stockId, portfolioId },
    skip: !stockId || !portfolioId,
    fetchPolicy: 'network-only',
  });
  const transactions = data?.stockTransactions || [];

  const refreshAfterSave = async (result) => {
    if (result.stockId && result.stockId !== stockId) {
      setStockId(result.stockId);
      await onImported?.(result.stockId);
    } else {
      await refetch();
    }
    await onChanged?.();
  };

  const handleToggleHidden = async (transaction) => {
    try {
      const response = await setTransactionHidden({
        variables: {
          transactionId: transaction.id,
          portfolioId,
          hidden: !transaction.hidden,
        },
      });
      const result = response.data?.setStockTransactionHidden;
      if (!result?.success) throw new Error(result?.error || 'Failed to update transaction');
      await refetch();
      await onChanged?.();
    } catch (hiddenError) {
      toast.error(hiddenError.message);
    }
  };

  const sortedTransactions = [...transactions].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
  );
  const displayToken = { ticker: stock.symbol };

  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-gray-100 shadow-md dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Transactions</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {transactions.length} transaction{transactions.length === 1 ? '' : 's'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          disabled={!portfolioId}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add transaction
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800">
        {transactions.some((tx) => tx.accountingWarning) && <div className="p-3"><StockAccountingWarning /></div>}
        {loading ? (
          <LoadingSpinner message="Loading transactions..." />
        ) : error ? (
          <div className="p-4"><ErrorAlert message={`Error loading transactions: ${error.message}`} /></div>
        ) : transactions.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">No stock transactions yet.</p>
            <button type="button" onClick={() => setShowAdd(true)} className="mt-3 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
              Record the first transaction
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-2 p-3 md:hidden">
              {sortedTransactions.map((transaction) => (
                <TransactionMobileCard
                  key={transaction.id}
                  transaction={transaction}
                  token={displayToken}
                  currentPrice={portfolioCurrentPrice}
                  tokenLocations={locations}
                  onEdit={() => setEditing(transaction)}
                  onDelete={() => setDeleting(transaction)}
                  onToggleEnabled={handleToggleHidden}
                  isExpanded={expandedTransactionId === transaction.id}
                  onToggle={() => setExpandedTransactionId((current) => (
                    current === transaction.id ? null : transaction.id
                  ))}
                />
              ))}
            </div>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr className="border-b dark:border-gray-700">
                    {['Date', 'Type', 'Price', 'Quantity', 'Cost', 'Proceeds', 'Fees', 'Realized PnL', 'Unrealized PnL', 'Location', 'Notes', 'Actions'].map((label) => (
                      <th
                        key={label}
                        className={`px-2 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 ${
                          ['Price', 'Quantity', 'Cost', 'Proceeds', 'Fees', 'Realized PnL', 'Unrealized PnL', 'Location', 'Notes'].includes(label)
                            ? 'text-right'
                            : label === 'Actions' ? 'text-center' : 'text-left'
                        }`}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {sortedTransactions.map((transaction) => (
                    <TransactionRow
                      key={transaction.id}
                      transaction={transaction}
                      token={displayToken}
                      currentPrice={portfolioCurrentPrice}
                      tokenLocations={locations}
                      onEdit={() => setEditing(transaction)}
                      onDelete={() => setDeleting(transaction)}
                      onToggleEnabled={handleToggleHidden}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <StockTransactionModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        stockId={stockId}
        portfolioId={portfolioId}
        stockCurrency={stock.currency}
        currentPrice={currentPrice}
        onSaved={refreshAfterSave}
      />
      <StockTransactionModal
        isOpen={Boolean(editing)}
        onClose={() => setEditing(null)}
        stockId={stockId}
        portfolioId={portfolioId}
        stockCurrency={stock.currency}
        currentPrice={currentPrice}
        transaction={editing}
        onSaved={refreshAfterSave}
      />

      <StockDeleteTransactionDialog
        transaction={deleting}
        symbol={stock.symbol}
        portfolioId={portfolioId}
        onClose={() => setDeleting(null)}
        onDeleted={async () => {
          await refetch();
          await onChanged?.();
        }}
      />
    </section>
  );
};

export default StockTransactions;
